import { Injectable } from '@angular/core';
import {
    QueryRequest,
    QueryQueryParameters,
    QueryResponse,
    ReportingApiService,
} from '@kempoweroyj/ngx-reporting-api-client';

interface RequestOptions {
    headers?: { [key: string]: string };
}

/**
 * Drop-in replacement for ReportingApiService.query() that routes to purpose-built endpoints.
 * Known src values are translated to the matching typed client method; unknown ones fall
 * through to the legacy /query endpoint.
 */
@Injectable({ providedIn: 'root' })
export class ReportingQueryService {
    constructor(private readonly reportingApi: ReportingApiService) {}

    /**
     * Same signature as ReportingApiService.query().
     * Routes to new endpoints based on src value.
     */
    async query(
        body: QueryRequest,
        queryParams?: QueryQueryParameters,
        options?: RequestOptions
    ): Promise<QueryResponse> {
        const routed = this.routeToNewEndpoint(queryParams?.src, body.query, queryParams);
        if (routed !== null) {
            // Routed calls don't forward `options` (custom headers / impersonation): the typed
            // routes are dispatched without it. Callers that need options — e.g. the sysadmin
            // withToken pattern — must call the typed ReportingApiService methods directly
            // (which support withToken and an options arg), not this shim.
            if (options && Object.keys(options).length > 0) {
                console.warn(
                    `ReportingQueryService: 'options' is ignored on routed src='${queryParams?.src}' — ` +
                        'call the typed ReportingApiService method directly if you need headers/options.'
                );
            }
            return routed;
        }

        // Fallback to legacy endpoint for unmapped src values
        return this.reportingApi.query(body, queryParams, options);
    }

    private routeToNewEndpoint(
        src: string | undefined,
        rawQuery: any,
        queryParams?: QueryQueryParameters
    ): Promise<QueryResponse> | null {
        if (!src) return null;

        // Extract common fields from raw query parameters
        const stationId = this.extractTermValue(rawQuery.parameters, 'stationId');
        // Station-scoped endpoints take the id in the path; in practice these src values always carry one.
        const pathStationId = stationId ?? '';
        const from = this.extractRangeBound(rawQuery.parameters, 'gte');
        const to = this.extractRangeBound(rawQuery.parameters, 'lte');
        const locationUid = this.extractTermValue(rawQuery.parameters, 'locationUid');

        switch (src) {
            // === Locations ===
            case 'locations':
                return this.reportingApi.locations({
                    size: rawQuery.size?.toString(),
                    includeDeleted: queryParams?.includeDeleted?.toString(),
                });

            // === Station Metrics ===
            case 'boot-times':
                return this.reportingApi.stationMetrics(pathStationId, { type: 'boot-times', from, to });
            case 'station-powergraph':
                return this.reportingApi.stationMetrics(pathStationId, {
                    type: 'power-graph',
                    from,
                    to,
                    interval: this.extractHistogramInterval(rawQuery.aggregations),
                });
            case 'station-historic':
                return this.reportingApi.stationMetrics(pathStationId, { type: 'historic', from, to });
            case 'hardware-pin-temp': {
                // The one hardware graph builds three different field projections under this src;
                // the backend can't tell them apart, so derive the subtype from the exists filter.
                const subtype = this.pinTempSubtype(rawQuery);
                return this.reportingApi.stationMetrics(pathStationId, {
                    type: 'pin-temp',
                    from,
                    to,
                    subtype,
                    satelliteId: this.extractTermValue(rawQuery.parameters, 'satelliteId')?.toString(),
                    connectorId: subtype === 'pin' ? this.ccsConnectorId(rawQuery) : undefined,
                });
            }
            case 'liquid-cooling-system-temps':
                return this.reportingApi.stationMetrics(pathStationId, {
                    type: 'liquid-cooling',
                    from,
                    to,
                    satelliteId: this.extractTermValue(rawQuery.parameters, 'satelliteId')?.toString(),
                    connectorId: this.ccsConnectorId(rawQuery),
                });
            case 'pg-meter-current-a': {
                // The power-group meter is its own entity (own endpoint /power-meters/{meterId});
                // the legacy raw query had a {term:{meterId}} filter — lift it into the path.
                const meterId = this.extractTermValue(rawQuery.parameters, 'meterId');
                return this.reportingApi.powerMeters(meterId ?? '');
            }
            case 'ocpp-bridge':
                return this.reportingApi.stationMetrics(pathStationId, { type: 'online-status' });

            // === Usage ===
            case 'usage-calendar':
                return this.reportingApi.usage({
                    type: 'calendar',
                    from,
                    to,
                    stationIds: this.extractTermsValue(rawQuery.parameters, 'stationId'),
                    interval: this.extractHistogramInterval(rawQuery.aggregations),
                });
            case 'usage-energy-total':
                return this.reportingApi.usage({
                    type: 'energy-total',
                    stationId,
                    stationIds: this.extractTermsValue(rawQuery.parameters, 'stationId'),
                });
            case 'usage-energy-30d':
                return this.reportingApi.usage({
                    type: 'energy-30d',
                    from,
                    to,
                    stationId,
                    stationIds: this.extractTermsValue(rawQuery.parameters, 'stationId'),
                });
            case 'usage-peak-power': {
                // Two different queries share this src — pick the type based on the index the caller chose
                const peakType =
                    rawQuery.indexName === 'schema-systemmetricshistory' ? 'peak-power-metrics' : 'peak-power';
                return this.reportingApi.usage({
                    type: peakType,
                    from,
                    to,
                    stationId,
                    stationIds: this.extractTermsValue(rawQuery.parameters, 'stationId'),
                });
            }
            case 'ai-stats-energy':
                return this.reportingApi.usage({
                    type: 'ai-energy',
                    from,
                    to,
                    stationId,
                    interval: this.extractHistogramInterval(rawQuery.aggregations),
                });
            case 'ai-stats-power':
                return this.reportingApi.usage({
                    type: 'ai-power',
                    from,
                    to,
                    stationId,
                    interval: this.extractHistogramInterval(rawQuery.aggregations),
                });
            case 'ev-stats':
                return this.reportingApi.usage({
                    type: 'ev-stats',
                    from,
                    to,
                    evMacAddresses: this.extractTermsValue(rawQuery.parameters, 'evMacAddress'),
                });
            case 'ev-usage-timeline':
                return this.reportingApi.usage({
                    type: 'ev-timeline',
                    from,
                    to,
                    evMacAddresses: this.extractTermsValue(rawQuery.parameters, 'evMacAddress'),
                    interval: this.extractHistogramInterval(rawQuery.aggregations),
                });
            case 'evs-all-stats':
                return this.reportingApi.usage({
                    type: 'evs-all',
                    from,
                    to,
                    stationIds: this.extractTermsValue(rawQuery.parameters, 'stationId'),
                });
            case 'hardware-evse-usage':
                return this.reportingApi.usage({ type: 'evse-usage', from, to, stationId });
            case 'token-usage':
                // The token-list dialog filters to the user's owned tokens via
                // `{terms:{idToken:[...]}}`. The shim must forward that list as the typed
                // idTokens param — without it the backend's byToken terms agg returns the
                // top-N idTokens in the tenant (cross-user/cross-org leak).
                return this.reportingApi.usage({
                    type: 'token-usage',
                    from,
                    to,
                    stationId,
                    idTokens: this.extractTermsValue(rawQuery.parameters, 'idToken'),
                });
            // reporting-widget is no longer routed through the shim — the dashboard-widget
            // service now calls reportingApi.usage({type:'widget', perspective, …}) directly with
            // typed perspective/breakBy/displays/timeInterval/itemCount/evseTypes params. The shim
            // case was dropped because the FE never built a typed-extractable raw query for this
            // src; the aggregation shape was baked into the body and unrecoverable by the shim.

            // === Station History (device replacements + transaction/status events) ===
            case 'station-historic-devices':
                return this.reportingApi.stationHistory(pathStationId, {
                    type: 'devices',
                    before: this.extractRangeLteField(rawQuery.parameters, 'versionCreatedTimestamp'),
                });
            case 'station-historic-tx':
                return this.reportingApi.stationHistory(pathStationId, {
                    type: 'transactions',
                    before: to || this.extractRangeLteField(rawQuery.parameters, 'timestamp'),
                });

            // === Transactions (POST) ===
            case 'schedule-tx':
                // The schedule timeline scopes by EV MAC (+ optional location) over a split
                // updateTime>=from / startTime<=to window — not by stationId. `from`/`to` resolve
                // to updateTime.gte / startTime.lte (the only gte / lte bounds in the query).
                return this.reportingApi.transactions({
                    type: 'schedule',
                    evMacAddresses: this.extractEvMacAddresses(rawQuery.parameters),
                    locationUid,
                    from,
                    to,
                    searchAfter: rawQuery.searchAfter,
                    size: rawQuery.size,
                });
            case 'sessions-search-page':
                return this.reportingApi.transactions({
                    type: 'sessions',
                    from,
                    to,
                    stationIds: this.extractTermsArray(rawQuery.parameters, 'stationId'),
                    searchAfter: rawQuery.searchAfter,
                    size: rawQuery.size,
                });
            case 'evs-unknown-ones':
                return this.reportingApi.transactions({
                    type: 'unknown-evs',
                    evMacAddresses: this.extractEvMacAddresses(rawQuery.parameters),
                    size: rawQuery.size,
                });
            case 'ev-related-stations':
                return this.reportingApi.transactions({
                    type: 'related-stations',
                    evMacAddresses: this.extractEvMacAddresses(rawQuery.parameters),
                    size: rawQuery.size,
                });
            case 'ev-estimate-battery-kwh':
                return this.reportingApi.transactions({
                    type: 'battery-estimate',
                    evMacAddresses: this.extractEvMacAddresses(rawQuery.parameters),
                    size: rawQuery.size,
                });

            // === Station Logs ===
            case 'syslog-check':
            case 'syslog-check-end': {
                // The fetch-logs flow probes for the specific sessmgr log line that proves "we
                // already have logs for this EVSE". Previously the shim dropped every probe-
                // defining input (satellite scope, SYSLOG_IDENTIFIER list, query_string match)
                // and the probe degraded to "any logStream entry in the window" — true for any
                // active station, so the fetch-logs flow short-circuits and the button stops
                // working. Recover the inputs from the raw query so the backend can rebuild the
                // proper probe. The end probe omits connectorId/evseType (no query_string).
                const probeInputs = this.extractSyslogCheckInputs(rawQuery);
                return this.reportingApi.stationLogs(pathStationId, {
                    type: 'syslog-check',
                    from,
                    to,
                    ...probeInputs,
                });
            }
            case 'syslog-lines':
                // The per-EVSE log-progress indicator needs the satellite scope too. Without it
                // the indicator counted logs from every satellite on the station.
                return this.reportingApi.stationLogs(pathStationId, {
                    type: 'syslog-lines',
                    from,
                    to,
                    ...this.pageParams(rawQuery),
                    sources: this.extractTermsValue(rawQuery.parameters, 'log_payload.source.id.keyword'),
                    units: this.extractTermsValue(rawQuery.parameters, 'log_payload.SYSLOG_IDENTIFIER.keyword'),
                    satelliteId: this.extractSatelliteId(rawQuery.parameters),
                });
            case 'syslog-pkgs':
                return this.reportingApi.stationLogs(pathStationId, { type: 'syslog-packages', from, to });
            case 'syslog-logs':
                // Log browser supports multi-source / multi-unit chip rows AND a free-text
                // "message contains" search box. The previous shim only read the singular
                // `term:source` / `term:unit` shapes (FE never sends those) and the text search
                // was dropped entirely.
                return this.reportingApi.stationLogs(pathStationId, {
                    type: 'syslog-browse',
                    from,
                    to,
                    ...this.pageParams(rawQuery),
                    sources: this.extractTermsValue(rawQuery.parameters, 'log_payload.source.id.keyword'),
                    units: this.extractTermsValue(rawQuery.parameters, 'log_payload.SYSLOG_IDENTIFIER.keyword'),
                    payload: this.extractTextFilter(rawQuery.parameters, 'message'),
                    // Forward the log browser's Timestamp sort toggle. Without this the typed
                    // handler always defaulted to desc, so flipping the arrow did nothing.
                    sortDirection: this.extractSortDirection(rawQuery.sort),
                });
            case 'syslog-sources':
                // Chip counts must mirror the currently-applied unit + message-text + time-range
                // filters — without them the counts were unfiltered tenant-wide and didn't
                // match the visible log rows.
                return this.reportingApi.stationLogs(pathStationId, {
                    type: 'syslog-sources',
                    from,
                    to,
                    units: this.extractTermsValue(rawQuery.parameters, 'log_payload.SYSLOG_IDENTIFIER.keyword'),
                    payload: this.extractTextFilter(rawQuery.parameters, 'message'),
                });
            case 'syslog-units':
                return this.reportingApi.stationLogs(pathStationId, {
                    type: 'syslog-units',
                    from,
                    to,
                    sources: this.extractTermsValue(rawQuery.parameters, 'log_payload.source.id.keyword'),
                    payload: this.extractTextFilter(rawQuery.parameters, 'message'),
                });
            case 'ocpplog':
                return this.reportingApi.stationLogs(pathStationId, {
                    type: 'ocpp',
                    from,
                    to,
                    ...this.pageParams(rawQuery),
                    messageId: this.extractTermValue(rawQuery.parameters, 'messageId'),
                    action: this.extractTermValue(rawQuery.parameters, 'action'),
                    source: this.extractTermValue(rawQuery.parameters, 'source'),
                    // messageTypeId (CALL=2/CALLRESULT=3/CALLERROR=4) was dropped previously —
                    // the Type filter silently did nothing.
                    messageTypeId: this.extractTermValue(rawQuery.parameters, 'messageTypeId')?.toString(),
                    payload: this.extractTextFilter(rawQuery.parameters, 'message'),
                });
            case 'ocpplog-actions':
                // Action dropdown enumerates available actions WITHIN the currently-applied
                // filter context (same filters as the visible ocpp log list, minus `action`
                // itself). Previously the dropdown computed over an unfiltered docset and
                // surfaced actions yielding zero hits.
                return this.reportingApi.stationLogs(pathStationId, {
                    type: 'ocpp-actions',
                    from,
                    to,
                    messageId: this.extractTermValue(rawQuery.parameters, 'messageId'),
                    source: this.extractTermValue(rawQuery.parameters, 'source'),
                    messageTypeId: this.extractTermValue(rawQuery.parameters, 'messageTypeId')?.toString(),
                    payload: this.extractTextFilter(rawQuery.parameters, 'message'),
                });

            // === OCPI Log (via stationLogs) ===
            case 'ocpilog':
                return this.reportingApi.stationLogs(pathStationId, {
                    type: 'ocpi',
                    from,
                    to,
                    size: rawQuery.size?.toString(),
                    messageId: this.extractTermValue(rawQuery.parameters, 'messageId'),
                    action: this.extractTextFilter(rawQuery.parameters, 'action'),
                    source: this.extractTermValue(rawQuery.parameters, 'source'),
                    ocpiType: this.extractTermValue(rawQuery.parameters, 'type'),
                    payload: this.extractTextFilter(rawQuery.parameters, 'message'),
                });

            // === Schedules ===
            case 'schedules': {
                // The scheduling timeline scopes by `terms:evId` + an optional locationUid
                // should-group + plannedDepartureTime.gte / plannedArrivalTime.lte in nested
                // bool.should.bool.filter clauses. The shim previously forwarded only stationId
                // (never present here) and the backend returned every schedule in the tenant.
                const scheduleScope = this.extractActiveScheduleScope(rawQuery);
                return this.reportingApi.schedules({
                    type: 'active',
                    ...scheduleScope,
                    includeDeleted: queryParams?.includeDeleted?.toString(),
                });
            }
            case 'schedule-historic':
                // The history slider scopes by the schedule's id (the FE raw query is
                // `{term:{id: this.data.event.id}}`). The shim previously forwarded only
                // stationId/from/to — none of which the raw query carries — so the backend
                // returned the tenant's entire schedule-history list. The FE passes
                // `includeDeleted: true` so the slider can scrub through deleted versions.
                return this.reportingApi.schedules({
                    type: 'historic',
                    scheduleId: this.extractTermValue(rawQuery.parameters, 'id'),
                    stationId,
                    from,
                    to,
                    includeDeleted: queryParams?.includeDeleted?.toString(),
                });
            case 'schedules-original-event': {
                // The lane-expanded PLAN row pulls the schedule's ORIGINAL plan from
                // schema-evschedulehistory because the optimizer rewrites the active doc's
                // startSoc/segments to the EV's current SoC as the schedule executes. The FE
                // raw query asks for up to `size` versions sorted newest-first by
                // versionModifiedTimestamp+timestamp, optionally bounded by a
                // `lte: event.from - 30min` range on either field, then picks the oldest
                // version with a plan that's older than the cutoff.
                //
                // The previous shim dropped size/sort/range and the backend hardcoded size=1
                // with no sort — so the typed endpoint returned a recent post-rewrite version
                // and the PLAN row rendered as a 99→100 sliver instead of the planned curve.
                // Forward the three controls explicitly so the contract matches the legacy
                // raw-query behaviour.
                return this.reportingApi.schedules({
                    type: 'original-event',
                    scheduleId: this.extractTermValue(rawQuery.parameters, 'id'),
                    includeDeleted: queryParams?.includeDeleted?.toString(),
                    versionBefore: this.extractVersionBefore(rawQuery.parameters),
                    size: rawQuery.size?.toString(),
                    sort: this.extractSortDirection(rawQuery.sort),
                });
            }

            // === Alerts ===
            case 'alertHistory':
                // A station target is a plain stationId term; a location target nests its uid (and
                // any member-station ids) in a bool.should. endTime is an independent range.
                return this.reportingApi.alerts({
                    type: 'history',
                    stationId,
                    locationUid: this.extractLocationUid(rawQuery.parameters),
                    stationIds: this.extractLocationStationIds(rawQuery.parameters),
                    from: this.extractRangeFieldBound(rawQuery.parameters, 'timestamp', 'gte'),
                    to: this.extractRangeFieldBound(rawQuery.parameters, 'timestamp', 'lte'),
                    endTimeFrom: this.extractRangeFieldBound(rawQuery.parameters, 'endTime', 'gte'),
                    endTimeTo: this.extractRangeFieldBound(rawQuery.parameters, 'endTime', 'lte'),
                    size: rawQuery.size?.toString(),
                });
            case 'connector-dialog-availability-1':
            case 'connector-dialog-availability-2':
                return this.reportingApi.alerts({
                    type: 'availability',
                    stationId,
                    evseId: this.extractTermValue(rawQuery.parameters, 'evseId'),
                    from,
                    to,
                });
            case 'externalNotificationsLog':
                return this.reportingApi.alerts({
                    type: 'notifications',
                    notificationType: this.extractTermValue(rawQuery.parameters, 'type'),
                    from,
                    to,
                    size: rawQuery.size?.toString(),
                });
            // === Audit Log (dedicated endpoint, own resource) ===
            case 'auditLog':
                // The audit-log dialog scopes by station / location / user (or org-wide when none
                // are set), an optional OR-group of include types, and "load more" offset paging.
                // The username term targets the keyword field. excludeInternals drops API-originated
                // entries. The caller never supplies the tenant — buildQuery injects it server-side.
                return this.reportingApi.auditLog({
                    stationId,
                    locationUid,
                    username: this.extractTermValue(rawQuery.parameters, 'user.userName.keyword'),
                    types: this.extractAuditLogTypes(rawQuery.parameters),
                    excludeInternals: this.hasInternalExclude(rawQuery) ? 'true' : undefined,
                    offset: rawQuery.from?.toString(),
                    size: rawQuery.size?.toString(),
                });
            case 'tokenAuditList':
                // The org API-token list reads a single audit-log type (GENERATE-REFRESH-TOKEN)
                // newest-first, scoped to the *viewed* organization (which may differ from the
                // caller's auth tenant for a sysadmin browsing another org). The viewed-org id
                // is forwarded as the typed `tenantId` param; the backend rejects the override
                // with 401 unless the caller has the systemAdmin role (self-referential overrides
                // are accepted as a no-op so non-sysadmins on their own org still work).
                return this.reportingApi.auditLog({
                    types: this.extractTermValue(rawQuery.parameters, 'type'),
                    size: rawQuery.size?.toString(),
                    tenantId: this.extractTermsArray(rawQuery.parameters, 'tenantIds')?.[0],
                });

            // === Station Labels ===
            case 'common-labels': {
                // The common-labels-edit dialog opens on either a station or a location entity
                // (FE raw query targets `schema-${type}` with `{term:{schema:type}}`). Read the
                // schema term so the backend hits the right index — hardcoding station meant the
                // Location labels editor silently showed station labels.
                const entity =
                    this.extractTermValue(rawQuery.parameters, 'schema') === 'location' ? 'location' : 'station';
                return this.reportingApi.stationLabels({ type: 'labels', entity });
            }
            case 'order-dialog-stations-for-order':
                // The order dialog filters by {term: {'manufacturingInfo.orderNumber': N}} — the
                // shim must extract the orderNumber, not a stationIds list (the FE doesn't know
                // the station ids yet; that's what this query is finding). Falling back to a
                // stationIds extraction returned `undefined` and the backend then served
                // "first 100 stations in the tenant" as the order's stations.
                return this.reportingApi.stationLabels({
                    type: 'stations-for-order',
                    orderNumber: this.extractTermValue(rawQuery.parameters, 'manufacturingInfo.orderNumber'),
                    stationIds: this.extractTermsValue(rawQuery.parameters, 'stationId'),
                });

            default:
                return null; // Not mapped — fall through to legacy
        }
    }

    /** Cursor-pagination params shared by the doc-fetch log queries, JSON-encoded for the query string. */
    private pageParams(rawQuery: any): { size?: string; searchAfter?: string; pit?: string } {
        return {
            size: rawQuery.size?.toString(),
            searchAfter: rawQuery.searchAfter ? JSON.stringify(rawQuery.searchAfter) : undefined,
            pit: rawQuery.pit ? JSON.stringify(rawQuery.pit) : undefined,
        };
    }

    // === Parameter extraction helpers ===

    /** Extract a term filter value: { term: { field: value } } */
    private extractTermValue(parameters: any[], field: string): string | undefined {
        if (!parameters) return undefined;
        for (const p of parameters) {
            if (p?.term?.[field] !== undefined) {
                const val = p.term[field];
                return typeof val === 'object' ? val.value : val;
            }
        }
        return undefined;
    }

    /** Extract a terms filter value as comma-separated string: { terms: { field: [...] } } */
    private extractTermsValue(parameters: any[], field: string): string | undefined {
        const arr = this.extractTermsArray(parameters, field);
        return arr ? arr.join(',') : undefined;
    }

    /** Extract a terms filter as array */
    private extractTermsArray(parameters: any[], field: string): string[] | undefined {
        if (!parameters) return undefined;
        for (const p of parameters) {
            if (p?.terms?.[field]) {
                return p.terms[field];
            }
        }
        return undefined;
    }

    /** EV charging-port MACs, from either a `terms` (multi) or a single `term` evMacAddress filter. */
    private extractEvMacAddresses(parameters: any[]): string[] | undefined {
        const many = this.extractTermsArray(parameters, 'evMacAddress');
        if (many) return many;
        const one = this.extractTermValue(parameters, 'evMacAddress');
        return one ? [one] : undefined;
    }

    /** Which pin-temp series the hardware graph requested, inferred from its `exists` filter. */
    private pinTempSubtype(rawQuery: any): 'pin' | 'board-core' | 'pmc' {
        const existsFields: string[] = (rawQuery.parameters || [])
            .map((p: any) => p?.exists?.field)
            .filter((f: any): f is string => typeof f === 'string');
        if (existsFields.includes('metrics.temp')) return 'board-core';
        if (existsFields.includes('metrics.pmcStatus.canAddress')) return 'pmc';
        return 'pin';
    }

    /** Connector index N parsed from a `ccsN` segment in the exists filter or the source projection. */
    private ccsConnectorId(rawQuery: any): string | undefined {
        const candidates: string[] = [];
        for (const p of rawQuery.parameters || []) {
            if (typeof p?.exists?.field === 'string') candidates.push(p.exists.field);
        }
        if (Array.isArray(rawQuery.source)) candidates.push(...rawQuery.source);
        for (const field of candidates) {
            const match = /ccs(\d+)/.exec(field);
            if (match) return match[1];
        }
        return undefined;
    }

    /** A specific range bound on a named field (e.g. timestamp.gte), coerced to a string. */
    private extractRangeFieldBound(parameters: any[], fieldName: string, bound: 'gte' | 'lte'): string | undefined {
        if (!parameters) return undefined;
        for (const p of parameters) {
            const value = p?.range?.[fieldName]?.[bound];
            if (value !== undefined) return typeof value === 'string' ? value : String(value);
        }
        return undefined;
    }

    /** locationUid from a top-level term, or nested in the alert-history location bool.should. */
    private extractLocationUid(parameters: any[]): string | undefined {
        const top = this.extractTermValue(parameters, 'locationUid');
        if (top !== undefined) return top;
        for (const p of parameters || []) {
            for (const clause of p?.bool?.should || []) {
                if (clause?.term?.locationUid !== undefined) {
                    const value = clause.term.locationUid;
                    return typeof value === 'object' ? value.value : value;
                }
            }
        }
        return undefined;
    }

    /** Member-station ids from the alert-history location bool.should terms, comma-joined. */
    private extractLocationStationIds(parameters: any[]): string | undefined {
        for (const p of parameters || []) {
            for (const clause of p?.bool?.should || []) {
                if (clause?.terms?.stationId) return clause.terms.stationId.join(',');
            }
        }
        return undefined;
    }

    /** Audit-log include-filter types: the dialog nests them in a bool.should of {term:{type}} clauses. */
    private extractAuditLogTypes(parameters: any[]): string | undefined {
        for (const p of parameters || []) {
            const should = p?.bool?.should;
            if (Array.isArray(should)) {
                const types = should
                    .map((c: any) => c?.term?.type)
                    .filter((t: any): t is string => typeof t === 'string');
                if (types.length) return types.join(',');
            }
        }
        return undefined;
    }

    /** True when an audit-log query excludes internal API-originated entries (source = 'API request'). */
    private hasInternalExclude(rawQuery: any): boolean {
        return (rawQuery.excludes || []).some((e: any) => e?.term?.source === 'API request');
    }

    /** Reverse-engineer the active-schedules scope from the FE's nested raw query.
     *  Pulls:
     *  - evIds (csv), from `{terms: {evId: [...]}}`.
     *  - locationUid, from the locationUid should-group's `{term: {locationUid}}` clause.
     *  - windowFrom / windowTo, from the plannedDepartureTime.gte / plannedArrivalTime.lte
     *    ranges that sit inside the evSchedule branch of the outer bool.should.bool.filter. */
    private extractActiveScheduleScope(rawQuery: any): {
        evIds?: string;
        locationUid?: string;
        windowFrom?: string;
        windowTo?: string;
    } {
        const params: any[] = rawQuery.parameters || [];

        // evIds — the FE always uses `terms` (plural). Drop a single-`term` fallback because the
        // raw query never produces one for this case.
        const evIdsArr = this.extractTermsArray(params, 'evId');
        const evIds = evIdsArr ? evIdsArr.join(',') : undefined;

        // locationUid — nested in a `bool.should` alongside a must_not-exists fallback for
        // legacy schedules without the field. Read the locationUid term out of the should array.
        let locationUid: string | undefined;
        for (const p of params) {
            for (const clause of p?.bool?.should || []) {
                if (clause?.term?.locationUid !== undefined) {
                    const v = clause.term.locationUid;
                    locationUid = typeof v === 'object' ? v.value : v;
                    break;
                }
            }
            if (locationUid !== undefined) break;
        }

        // windowFrom / windowTo — inside the evSchedule branch's bool.filter list. The outer
        // shape is `parameters[].bool.should[].bool.filter[]` carrying the plannedDepartureTime
        // and plannedArrivalTime range clauses.
        let windowFrom: string | undefined;
        let windowTo: string | undefined;
        for (const p of params) {
            for (const branch of p?.bool?.should || []) {
                for (const filter of branch?.bool?.filter || []) {
                    const gte = filter?.range?.plannedDepartureTime?.gte;
                    const lte = filter?.range?.plannedArrivalTime?.lte;
                    if (gte !== undefined) windowFrom = String(gte);
                    if (lte !== undefined) windowTo = String(lte);
                }
            }
        }

        return { evIds, locationUid, windowFrom, windowTo };
    }

    /** Upper-bound timestamp for the original-event lookup. The FE raw query bounds the history
     *  walk with either:
     *    - `bool.should: [{range:{versionModifiedTimestamp:{lte:X}}}, {range:{timestamp:{lte:X}}}]`
     *      (normal lane-expanded PLAN row + history-slider's nowTs path), or
     *    - `bool.must.range.versionModifiedTimestamp.lte` (history-slider's nowTs path collapses
     *      to a single-field must when only that field is set).
     *  Without this bound the typed endpoint returns post-rewrite versions and the PLAN row
     *  renders as a 99→100 sliver instead of the user's planned curve. */
    private extractVersionBefore(parameters: any[]): string | undefined {
        if (!parameters) return undefined;
        for (const p of parameters) {
            for (const clause of p?.bool?.should ?? []) {
                const ver = clause?.range?.versionModifiedTimestamp?.lte;
                if (ver !== undefined) return String(ver);
                const ts = clause?.range?.timestamp?.lte;
                if (ts !== undefined) return String(ts);
            }
            const must = p?.bool?.must;
            const mustArr = Array.isArray(must) ? must : must ? [must] : [];
            for (const m of mustArr) {
                const ver = m?.range?.versionModifiedTimestamp?.lte;
                if (ver !== undefined) return String(ver);
            }
        }
        return undefined;
    }

    /** First sort entry's direction, from any of the ES encodings the FE emits:
     *  `{field:{order:'asc'}}` (alerts/audit/schedule bodies) and the shorthand `{field:'asc'}`
     *  (the syslog log-browser sends `[{timestamp:'asc'},{sequenceNo:'asc'}]`). Returns undefined
     *  when no direction is present, so callers fall back to the typed endpoint's own default. */
    private extractSortDirection(sort: any): 'asc' | 'desc' | undefined {
        if (!sort) return undefined;
        const sortArr = Array.isArray(sort) ? sort : [sort];
        for (const s of sortArr) {
            if (typeof s !== 'object' || s === null) continue;
            for (const key of Object.keys(s)) {
                const val = s[key];
                const dir = typeof val === 'string' ? val : val?.order;
                if (dir === 'asc' || dir === 'desc') return dir;
            }
        }
        return undefined;
    }

    /** Extract a satellite id from the satellite-or-self `bool.should.bool.filter` produced by
     *  the FE's getFilterForSatelliteOrSelf helper — used by syslog-check (start + end probes)
     *  and by syslog-lines (per-EVSE log progress counter). */
    private extractSatelliteId(parameters: any[]): string | undefined {
        for (const p of parameters || []) {
            for (const branch of p?.bool?.should || []) {
                for (const filter of branch?.bool?.filter || []) {
                    const id = filter?.term?.['log_payload.source.id'];
                    if (id !== undefined) return String(id);
                }
            }
        }
        return undefined;
    }

    /** Reverse-engineer the syslog-check probe inputs from the FE's raw query.
     *  Pulls:
     *  - satelliteId, from the bool.should produced by getFilterForSatelliteOrSelf (the SATELLITE
     *    branch's `{term:{'log_payload.source.id': N}}`).
     *  - syslogIdentifiers, from `{terms:{'log_payload.SYSLOG_IDENTIFIER.keyword': [...]}}`.
     *  - connectorId + evseType, from the `query_string.query` body if present (start probe). */
    private extractSyslogCheckInputs(rawQuery: any): {
        satelliteId?: string;
        connectorId?: string;
        evseType?: string;
        syslogIdentifiers?: string;
    } {
        const params: any[] = rawQuery.parameters || [];

        // satelliteId — descend into the bool.should built by getFilterForSatelliteOrSelf.
        let satelliteId: string | undefined;
        for (const p of params) {
            for (const branch of p?.bool?.should || []) {
                for (const filter of branch?.bool?.filter || []) {
                    const id = filter?.term?.['log_payload.source.id'];
                    if (id !== undefined) {
                        satelliteId = String(id);
                        break;
                    }
                }
                if (satelliteId !== undefined) break;
            }
            if (satelliteId !== undefined) break;
        }

        // syslogIdentifiers — multi-value list, the FE always uses `terms` not `term`.
        let syslogIdentifiers: string | undefined;
        for (const p of params) {
            const ids = p?.terms?.['log_payload.SYSLOG_IDENTIFIER.keyword'];
            if (Array.isArray(ids) && ids.length > 0) {
                syslogIdentifiers = ids.join(',');
                break;
            }
        }

        // connectorId + evseType — parsed out of `[S<sat> C<conn>]... Switching to new <type> context`.
        let connectorId: string | undefined;
        let evseType: string | undefined;
        for (const p of params) {
            const q = p?.query_string?.query;
            if (typeof q !== 'string') continue;
            const m = /"\[S(\d+) C(\d+)]".*"Switching to new (\w+) context"/.exec(q);
            if (m) {
                // satelliteId from the message overrides the bool.should reading (they should
                // always agree; the bool form is the canonical scope).
                if (!satelliteId) satelliteId = m[1];
                connectorId = m[2];
                evseType = m[3];
                break;
            }
        }

        return { satelliteId, connectorId, evseType, syslogIdentifiers };
    }

    /** Extract a range bound (gte/lte) from the first range filter, whatever field it targets. */
    private extractRangeBound(parameters: any[], bound: 'gte' | 'lte'): string | undefined {
        if (!parameters) return undefined;
        for (const p of parameters) {
            if (p?.range) {
                const field = Object.keys(p.range)[0];
                if (p.range[field]?.[bound]) return p.range[field][bound];
            }
        }
        return undefined;
    }

    /** Extract lte from a range on a specific field */
    private extractRangeLteField(parameters: any[], fieldName: string): string | undefined {
        if (!parameters) return undefined;
        for (const p of parameters) {
            if (p?.range?.[fieldName]?.lte) return p.range[fieldName].lte;
        }
        // Also check inside bool.should
        for (const p of parameters) {
            if (p?.bool?.should) {
                for (const s of p.bool.should) {
                    if (s?.range?.[fieldName]?.lte) return s.range[fieldName].lte;
                }
            }
        }
        return undefined;
    }

    /** Extract interval from aggregation histogram */
    private extractHistogramInterval(aggregations: any): string | undefined {
        if (!aggregations) return undefined;
        const findInterval = (obj: any): string | undefined => {
            if (!obj || typeof obj !== 'object') return undefined;
            if (obj.date_histogram) {
                return obj.date_histogram.fixed_interval || obj.date_histogram.calendar_interval;
            }
            for (const key of Object.keys(obj)) {
                const result = findInterval(obj[key]);
                if (result) return result;
            }
            return undefined;
        };
        return findInterval(aggregations);
    }

    /** Extract text filter value (wildcard/regexp/match_phrase) and encode for backend */
    private extractTextFilter(parameters: any[], field: string): string | undefined {
        if (!parameters) return undefined;
        for (const p of parameters) {
            if (p?.wildcard?.[field]) {
                const val = typeof p.wildcard[field] === 'object' ? p.wildcard[field].value : p.wildcard[field];
                // Strip surrounding wildcards since backend adds them
                return val.replace(/^\*|\*$/g, '');
            }
            if (p?.regexp?.[field]) {
                const val = typeof p.regexp[field] === 'object' ? p.regexp[field].value : p.regexp[field];
                return `/r/${val}`;
            }
            if (p?.match_phrase?.[field]) {
                return p.match_phrase[field];
            }
            // OCPP log search encodes payload as { match: { message: { query, operator: 'AND' } } }.
            if (p?.match?.[field]) {
                const m = p.match[field];
                return typeof m === 'object' ? m.query : m;
            }
        }
        return undefined;
    }
}
